import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { FloppyDisk, ArrowCounterClockwise, Eye, Globe } from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Grouped + labelled fields the admin sees in the UI
const SECTIONS = [
  {
    label: 'Ana Sayfa — Hero',
    icon: '🏠',
    fields: [
      { key: 'landing.hero.title', label: 'Hero Başlık', type: 'text' },
      { key: 'landing.hero.subtitle', label: 'Hero Alt Başlık', type: 'textarea' },
      { key: 'landing.hero.cta.primary', label: 'Ana CTA Butonu', type: 'text' },
      { key: 'landing.hero.cta.demo', label: 'Demo CTA Butonu', type: 'text' },
      { key: 'landing.hero.cta.secondary', label: 'İkincil CTA Butonu', type: 'text' },
    ],
  },
  {
    label: 'Ana Sayfa — Bölüm Başlıkları',
    icon: '📋',
    fields: [
      { key: 'landing.stats.headline', label: 'İstatistik Bölümü Başlığı', type: 'text' },
      { key: 'landing.howitworks.headline', label: 'Nasıl Çalışır Başlığı', type: 'text' },
      { key: 'landing.pricing.headline', label: 'Fiyatlandırma Başlığı', type: 'text' },
    ],
  },
  {
    label: 'Footer',
    icon: '⚓',
    fields: [
      { key: 'landing.footer.tagline', label: 'Footer Slogan', type: 'textarea' },
      { key: 'landing.footer', label: 'Telif Hakkı Satırı (© …)', type: 'text' },
      { key: 'landing.footer.payments', label: 'Ödeme Güvenlik Metni', type: 'text' },
    ],
  },
  {
    label: 'Sayfa İçerikleri (Markdown)',
    icon: '📄',
    fields: [
      { key: 'hakkimizda.body', label: 'Hakkımızda — Tam İçerik', type: 'markdown' },
      { key: 'iletisim.body', label: 'İletişim — Tam İçerik', type: 'markdown' },
    ],
  },
];

export default function SiteContentAdminPanel() {
  const { getAuthHeaders } = useAuth();
  const [lang, setLang] = useState('tr');
  const [overrides, setOverrides] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/admin/content`, { headers: getAuthHeaders() });
        const flat = {};
        for (const [key, byLang] of Object.entries(data.overrides || {})) {
          if (byLang[lang]) flat[key] = byLang[lang].value;
        }
        if (!cancelled) {
          setOverrides(flat);
          setDrafts(flat);
        }
      } catch (_e) {
        if (!cancelled) toast.error('İçerikler yüklenemedi');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const isDirty = useMemo(
    () => Object.keys(drafts).some((k) => (drafts[k] || '') !== (overrides[k] || '')),
    [drafts, overrides],
  );

  const save = async (key) => {
    const value = drafts[key] ?? '';
    setSavingKey(key);
    try {
      await axios.put(
        `${API_URL}/admin/content/${encodeURIComponent(key)}`,
        { value },
        { headers: getAuthHeaders(), params: { lang } },
      );
      setOverrides((prev) => ({ ...prev, [key]: value }));
      toast.success('Kaydedildi — site anında güncellendi');
      window.dispatchEvent(new CustomEvent('growthriq:content-updated'));
    } catch (_e) {
      toast.error('Kaydedilemedi');
    } finally {
      setSavingKey(null);
    }
  };

  const reset = async (key) => {
    setSavingKey(key);
    try {
      await axios.delete(`${API_URL}/admin/content/${encodeURIComponent(key)}`, {
        headers: getAuthHeaders(),
        params: { lang },
      });
      setOverrides((prev) => {
        const c = { ...prev };
        delete c[key];
        return c;
      });
      setDrafts((prev) => {
        const c = { ...prev };
        delete c[key];
        return c;
      });
      toast.success('Varsayılana döndü');
      window.dispatchEvent(new CustomEvent('growthriq:content-updated'));
    } catch (_e) {
      toast.error('Sıfırlanamadı');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="site-content-admin-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white border border-[#E4E4E7] p-4">
        <div>
          <h2 className="font-bold text-lg text-[#09090B] flex items-center gap-2">
            <Globe size={18} weight="duotone" className="text-[#3CD9B8]" />
            Site İçerikleri
          </h2>
          <p className="text-xs text-[#52525B] mt-1">
            Web sitenizdeki hero metni, footer ve önemli sayfaları buradan düzenleyin. Değişiklikler anında yayına girer.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#52525B]">Dil:</span>
          <div className="inline-flex rounded-md border border-[#E4E4E7] overflow-hidden">
            {['tr', 'en'].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`content-lang-${l}`}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${
                  lang === l ? 'bg-[#0F1B36] text-white' : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E4E4E7] p-12 text-center text-sm text-[#52525B]">Yükleniyor…</div>
      ) : (
        SECTIONS.map((section) => (
          <div key={section.label} className="bg-white border border-[#E4E4E7]">
            <div className="px-5 py-3 border-b border-[#E4E4E7] flex items-center gap-2">
              <span>{section.icon}</span>
              <h3 className="font-semibold text-sm text-[#09090B]">{section.label}</h3>
            </div>
            <div className="divide-y divide-[#F4F4F5]">
              {section.fields.map((f) => {
                const currentValue = drafts[f.key] ?? '';
                const savedValue = overrides[f.key] ?? '';
                const dirty = currentValue !== savedValue;
                const overridden = Object.prototype.hasOwnProperty.call(overrides, f.key);
                return (
                  <div key={f.key} className="px-5 py-4 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 items-start">
                    <div>
                      <p className="text-sm font-semibold text-[#09090B]">{f.label}</p>
                      <p className="text-[10px] text-[#52525B] font-mono mt-0.5">{f.key}</p>
                      {overridden && (
                        <span className="inline-block mt-1.5 text-[10px] uppercase tracking-wider bg-[#3CD9B8]/15 text-[#0F1B36] px-1.5 py-0.5 rounded">
                          Özelleştirildi
                        </span>
                      )}
                    </div>
                    <div>
                      {f.type === 'text' ? (
                        <input
                          type="text"
                          value={currentValue}
                          onChange={(e) => setDrafts((p) => ({ ...p, [f.key]: e.target.value }))}
                          placeholder="Varsayılan değer kullanılıyor — yazın ve kaydedin"
                          data-testid={`content-input-${f.key}`}
                          className="w-full px-3 py-2 border border-[#E4E4E7] text-sm focus:outline-none focus:ring-2 focus:ring-[#3CD9B8]"
                        />
                      ) : (
                        <textarea
                          value={currentValue}
                          onChange={(e) => setDrafts((p) => ({ ...p, [f.key]: e.target.value }))}
                          rows={f.type === 'markdown' ? 14 : 3}
                          placeholder={f.type === 'markdown' ? 'Markdown destekli — başlıklar için ## kullanabilirsiniz' : 'Varsayılan değer kullanılıyor'}
                          data-testid={`content-input-${f.key}`}
                          className={`w-full px-3 py-2 border border-[#E4E4E7] text-sm focus:outline-none focus:ring-2 focus:ring-[#3CD9B8] ${f.type === 'markdown' ? 'font-mono text-xs' : ''}`}
                        />
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => save(f.key)}
                          disabled={!dirty || savingKey === f.key}
                          data-testid={`content-save-${f.key}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-[#0F1B36] text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
                        >
                          <FloppyDisk size={12} weight="bold" />
                          {savingKey === f.key ? 'Kaydediliyor…' : 'Kaydet'}
                        </button>
                        {overridden && (
                          <button
                            onClick={() => reset(f.key)}
                            disabled={savingKey === f.key}
                            data-testid={`content-reset-${f.key}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-[#E4E4E7] text-[#52525B] text-xs font-semibold hover:bg-[#F4F4F5]"
                          >
                            <ArrowCounterClockwise size={12} weight="bold" /> Varsayılana dön
                          </button>
                        )}
                        {f.type === 'markdown' && (
                          <a
                            href={f.key.startsWith('hakkimizda') ? '/hakkimizda' : '/iletisim'}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-[#3CD9B8] hover:underline"
                          >
                            <Eye size={12} weight="bold" /> Önizle
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {isDirty && !loading && (
        <div className="sticky bottom-4 bg-amber-50 border-2 border-amber-300 text-amber-900 px-4 py-3 rounded-lg text-sm text-center">
          ⚠️ Kaydedilmemiş değişiklikler var — her satırın altındaki <strong>Kaydet</strong> butonunu kullanın.
        </div>
      )}
    </div>
  );
}
